import { FC, memo, SVGProps } from 'react';
import clsx from 'clsx';

import { getDeeltrajectRx } from '@/apps/mwd-v2/utils/deeltraject';
import { isZelfdeDienstregelpunt } from '@/utils/dienstregelpunt';

import { MwdDeeltraject } from '../../../types/Domain';

import styles from '../MwdChart.module.css';

type ClaimProps = SVGProps<SVGRectElement> & {
  claim: MwdDeeltraject;
  isSelected: boolean;
};

const Claim: FC<ClaimProps> = ({ claim, isSelected, ...rectProps }) => {
  const positionY = claim.inzetIsKort ? claim.trapHoogteIndex * 22 : claim.y;
  const baseWidth = claim.width;
  const baseHeight = 22;
  const rxOriginal =
    isZelfdeDienstregelpunt(claim.dienstregelpuntVan, claim.dienstregelpuntNaar) && !!claim.claimsoort
      ? 0
      : getDeeltrajectRx(baseWidth, 10);

  // Dynamic geometry based on selection
  const rectInset = isSelected ? Math.min(2, baseWidth / 4) : 0;
  const rectX = rectInset;
  const rectY = isSelected ? 2 : positionY;
  const rectWidth = baseWidth - rectInset * 2;
  const rectHeight = isSelected ? baseHeight - 4 : baseHeight;
  const rx = getDeeltrajectRx(rectWidth, Math.max(rxOriginal - rectInset, 0));

  return (
    <g
      transform={!isSelected ? undefined : `translate(0, ${positionY})`}
      data-testid={`claim-${claim.wleId}`}
      className={`${isSelected ? 'is-selected-v2' : ''}`}
      data-is-claim-selected={isSelected}
    >
      {/* Outer border only when selected */}
      {isSelected && (
        <rect
          x={-1.5}
          y={-1.5}
          width={baseWidth + 3}
          height={baseHeight + 3}
          rx={rxOriginal + 1.5}
          stroke='var(--tractie-color-border-selected-default)'
          strokeWidth={2}
          fill='transparent'
          pointerEvents='none'
        />
      )}

      <rect
        tabIndex={0}
        id={`wle-${claim.wleId}`}
        data-status={claim.variant}
        data-is-claim={true}
        x={rectX}
        y={rectY}
        width={rectWidth}
        height={rectHeight}
        rx={rx}
        stroke='var(--tractie-color-background-default)'
        strokeWidth={1.5}
        strokeDasharray='6,3'
        fill='transparent'
        className={clsx(
          styles.werklijnElementenStyleBase,
          styles[`werklijnElementenStyle${claim.variant.charAt(0).toUpperCase() + claim.variant.slice(1)}`],
          claim.versterkt && styles.versterkt,
          !!claim.claimsoort && styles.isClaimed,
        )}
        style={claim.claimsoort ? { fillOpacity: '0.1' } : undefined}
        {...rectProps}
      />
    </g>
  );
};

export default memo(Claim);
